import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useState } from "react";
import { faDownload, faEye, faInbox } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AG_GRID_LOCALE_PT_BR } from "../../../components/ListaParticipante/AgGridPTBR";
import "./style.css";
import servAnalises from "../../../services/login/servAnalises";
import Popup from "../../../components/Popup";
import { Box, CircularProgress } from "@mui/material";
import { style as modelStyle } from "../../../components/Loading/Loading";

const ListaAnexos = ({ anexosAnalise }) => {
    const [rowData, setRowData] = useState([]);
    const [openPopup, setOpenPopup] = useState(false);
    const [fileContent, setFileContent] = useState("");
    const [fileType, setFileType] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const initialColumnDef = [
        { field: 'NomeArquivo', headerName: 'Nome arquivo', width: 315 },
        { field: 'ExtensaoDocumentoParecer', headerName: 'Extensão', width: 92 },
        {
            headerName: 'Visualizar',
            cellRendererFramework: (params) => (
                <button
                    onClick={() => handleVisualizarAnexo(params.data)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    title="Visualizar Anexo"
                >
                    <div className="iconeAnexo">
                        <FontAwesomeIcon icon={faEye} size="lg" />
                    </div>
                </button>
            ),
            width: 97,
        },
        {
            headerName: 'Baixar',
            cellRendererFramework: (params) => (
                <button
                    onClick={() => handleDownloadAnexo(params.data)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    title="Baixar Anexo"
                >
                    <div className="iconeAnexo">
                        <FontAwesomeIcon icon={faDownload} size="lg" />
                    </div>
                </button>
            ),
            width: 90,
        }
    ];

    const [columnDefs, setColumnDefs] = useState(initialColumnDef);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        headerClass: "ag-left-aligned-header",
        resizable: true,
        cellStyle: { border: 'none' },
        suppressMenu: true,
    }), []);

    const localeText = useMemo(() => AG_GRID_LOCALE_PT_BR, []);

    useEffect(() => {
        setRowData(anexosAnalise || []);
    }, [anexosAnalise]);

    const handleVisualizarAnexo = async (anexo) => {
        const extensao = anexo.ExtensaoDocumentoParecer.toLowerCase();
        if (!validarExtensaoArquivo(extensao)) {
            handleDownloadAnexo(anexo);
            return;
        }

        let dto = {
            CodigoAnalise: anexo.CodigoSolicitacaoAnalise,
            CodigoDocumento: anexo.CodigoDocumento,
        };
    
        try {
            setIsLoading(true);
            let dados = await servAnalises.downloadAnexo(dto);
    
            const base64 = dados.Content.Arquivo;
            const extensao = anexo.ExtensaoDocumentoParecer.toLowerCase();
            const mimeType = getMimeType(dados.Content.Extensao);
            const fileURL = `data:${mimeType};base64,${base64}`;
            const byteCharacters = atob(base64);

            const byteNumbers = new Array(byteCharacters.length).fill().map((_, i) => byteCharacters.charCodeAt(i));
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mimeType });
            const blobURL = URL.createObjectURL(blob);
    
            setFileContent(fileURL);
            setFileType(mimeType);
    
            if (extensaoArquivosDocs.includes(extensao)) {
                window.open(blobURL, "_blank");
            } else if(extensao === "svg") {
                const svgURL = `data:image/svg+xml;base64,${base64}`;
                setFileContent(svgURL);
                setFileType(mimeType);
                setOpenPopup(true);
                return;
            }else {
                setOpenPopup(true);

            }
        } catch (err) {
            console.error("Erro ao visualizar o anexo:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const validarExtensaoArquivo = (extensao) => {
        let extensoesPermitidas = ['jpg', 'jfif', 'jpeg', 'gif', 'png', 'svg', 'psd', 'webp', 'raw', 'tiff', 'bmp', 'doc', 'docx', 'pdf', 'xls', 'xlsx', 'odt', 'rtf', 'xlsm', 'txt'];
    
        return extensoesPermitidas.includes(extensao);
    }

    const handleDownloadAnexo = async (anexo) => {
        let dto = {
            CodigoAnalise: anexo.CodigoSolicitacaoAnalise,
            CodigoDocumento: anexo.CodigoDocumento,
        };
        
        try {
            setIsLoading(true);
            let dados = await servAnalises.downloadAnexo(dto);
            
            const base64 = dados.Content.Arquivo;
            const mimeType = getMimeType(dados.Content.Extensao);
            const extensao = anexo.ExtensaoDocumentoParecer.toLowerCase();

            let fileName = dados.Content.Nome || "documento";

            if (!fileName.endsWith(`.${extensao}`)) {
                fileName += `.${extensao}`;
            }

            const fileURL = `data:${mimeType};base64,${base64}`;

            const link = document.createElement('a');
            link.href = fileURL;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setIsLoading(false);
        } catch (err) {
            console.error("Erro ao visualizar o anexo:", err);
            setIsLoading(false);
        }
    }

    const extensaoArquivosDocs = [
        'doc', 'docx', 'pdf', 'xls', 'xlsx', 'odt', 'rtf', 'xlsm', 'txt'
    ];

    const extensaoImagens = [
        "jpg", "jfif", "jpeg", "gif", "png", "svg", "psd", "webp", "raw", "tiff", "bmp"
    ];

    const getMimeType = (extensao) => {
        const mimeTypes = {
            pdf: "application/pdf",
            png: "image/png",
            jpg: "image/jpeg",
            jpeg: "image/jpeg",
            gif: "image/gif",
            svg: "image/svg+xml",
            psd: "image/vnd.adobe.photoshop",
            webp: "image/webp",
            raw: "image/x-raw",
            tiff: "image/tiff",
            bmp: "image/bmp",
            txt: "text/plain",
            doc: "application/msword",
            docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            xls: "application/vnd.ms-excel",
            xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            xlsm: "application/vnd.ms-excel.sheet.macroEnabled.12",
            odt: "application/vnd.oasis.opendocument.text",
            rtf: "application/rtf",
            jfif: "image/jpeg",
        };
    
        extensao = extensao.toLowerCase();
    
        return mimeTypes[extensao] || "application/octet-stream";
    };

    function decodeBase64(base64) {
        base64 = base64.replace(/\s/g, ''); 
        base64 = base64.replace(/^data:.*?;base64,/, '');
        const binaryString = window.atob(base64); 
        const byteArray = new Uint8Array(binaryString.length);
    

        for (let i = 0; i < binaryString.length; i++) {
            byteArray[i] = binaryString.charCodeAt(i);
        }
    

        const decoder = new TextDecoder('utf-8');
        return decoder.decode(byteArray);
    }

    return (
        <>
            <div className="ag-theme-alpine-dark" style={{ minHeight: 150, height: 405, maxHeight: 410 }}>
                <AgGridReact
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    localeText={localeText}
                    rowHeight={40}
                    headerHeight={50}
                />
            </div>

            {isLoading && (
                <Box style={modelStyle} >
                    <CircularProgress color="inherit" />
                </Box>
            )}

            <Popup
                title="Visualizar Anexo"
                openPopup={openPopup}
                setOpenPopup={setOpenPopup}
                minHeight="100%"
                minWidth="100%"
                hiddenCloseBtn={false}
            >
                {fileType.startsWith("application/pdf") ? (
                    <iframe
                        src={fileContent}
                        style={{ width: 600, height: 700, border: "none" }}
                    />
                ) : ["application/msword",
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                    "application/vnd.ms-excel",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    "application/vnd.ms-excel.sheet.macroEnabled.12",
                    "application/vnd.oasis.opendocument.text",
                    "application/rtf",
                    "text/plain"].includes(fileType) ? (
                    <iframe
                        src={`https://docs.google.com/gview?url=${fileContent}&embedded=true`}
                        style={{ width: "100%", height: "100%", border: "none" }}
                        title="Visualização de Documento"
                    />
                ) : extensaoImagens.includes(fileType.split("/")[1]) || fileType === "image/svg+xml" ? (
                    <img 
                        src={fileContent}
                        alt="Preview"
                        style={{ maxWidth: "100%", maxHeight: "100%" }}
                    />
                ) : fileType === "text/plain" ? (
                    <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word", width: '100%', height: '100%', backgroundColor: '#979797' }}>
                        {decodeBase64(fileContent)}
                    </pre>
                ) : (
                    <div className="SemAnexos" style={{ width: "100%", padding: "0 15px" }}>
                        <Box sx={{ mt: 1 }}>Visualização para este tipo de arquivo não está disponível.</Box>
                    </div>
                )}
            </Popup>
        </>
    );
};

export { ListaAnexos };
